import "core-js/modules/es.array.includes.js";
/**
 * PaginationHandler
 * Handles Mozaic V2 prev/next pagination:
 *  - injects the current filter param into pagination nav targets so it is preserved on page change
 *  - wires the page-selector <select> to navigate to the selected page
 *  - wires the prev/next buttons (data-href) to navigate
 */
export default class PaginationHandler {
  /**
   * @param {String} [containerSelector='.js-pagination'] - CSS selector for the pagination nav
   */
  constructor(containerSelector = '.js-pagination') {
    const pagination = document.querySelector(containerSelector);
    if (pagination) {
      this.updateLinksWithFilter(pagination);
      this.initSelectNavigation(pagination);
      this.initButtonNavigation(pagination);
    }
  }

  /**
   * Appends the current "filter" query param to every pagination nav target
   * (prev/next buttons' data-href) so that page navigation preserves the active filter.
   *
   * @param {Element} pagination
   */
  updateLinksWithFilter(pagination) {
    const filterParam = new URL(document.location).searchParams.get('filter');
    if (!filterParam) return;
    pagination.querySelectorAll('[data-href]').forEach(element => {
      const href = element.getAttribute('data-href');
      if (href && !href.includes('filter=')) {
        const separator = href.includes('?') ? '&' : '?';
        element.setAttribute('data-href', `${href}${separator}filter=${filterParam}`);
      }
    });
  }

  /**
   * Wires the Mozaic pagination <select class="mc-select__control"> so that
   * changing its value navigates to the corresponding page (preserving filter).
   *
   * @param {Element} pagination
   */
  initSelectNavigation(pagination) {
    const select = pagination.querySelector('.mc-select__control');
    if (!select) return;
    select.addEventListener('change', () => {
      const page = select.value;
      if (!/^\d+$/.test(page)) return;
      const filterParam = new URL(document.location).searchParams.get('filter');
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('p', page);
      if (filterParam) nextUrl.searchParams.set('filter', filterParam);
      window.location.assign(nextUrl.toString());
    });
  }

  /**
   * Wires the Mozaic pagination prev/next <button data-href="..."> elements to navigate.
   *
   * @param {Element} pagination
   */
  initButtonNavigation(pagination) {
    pagination.querySelectorAll('button[data-href]').forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        window.location.assign(button.dataset.href);
      });
    });
  }

  /**
   * Static helper – updates pagination nav targets inside a given container element
   * with a specific filter value (useful after an AJAX content reload).
   *
   * @param {Element} container  - the element that contains the .js-pagination nav
   * @param {String}  filterValue - encoded filter value (may be empty)
   */
  static updateContainerPaginationFilter(container, filterValue) {
    if (!filterValue) return;
    const pagination = container.querySelector('.js-pagination');
    if (!pagination) return;
    pagination.querySelectorAll('[data-href]').forEach(element => {
      const href = element.getAttribute('data-href');
      if (href && !href.includes('filter=')) {
        const separator = href.includes('?') ? '&' : '?';
        element.setAttribute('data-href', `${href}${separator}filter=${filterValue}`);
      }
    });
    const select = pagination.querySelector('.mc-select__control');
    if (select) {
      const newSelect = select.cloneNode(true);
      select.parentNode.replaceChild(newSelect, select);
      newSelect.addEventListener('change', () => {
        const page = newSelect.value;
        if (!/^\d+$/.test(page)) return;
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('p', page);
        if (filterValue) nextUrl.searchParams.set('filter', filterValue);
        window.location.assign(nextUrl.toString());
      });
    }
    pagination.querySelectorAll('button[data-href]').forEach(button => {
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      newButton.addEventListener('click', () => {
        if (newButton.disabled) return;
        window.location.assign(newButton.dataset.href);
      });
    });
  }
}